/**
 * Remembers that this client was presenting, so it can offer to resume after
 * the panel is rebuilt.
 *
 * Found on a handset on 2026-09-18. Machine A was presenting and the phone was
 * following; later a page turn on Machine A did not move the phone, and the
 * readout said Machine A was no longer presenting. Nobody pressed Stop --
 * `stopPresenting` has exactly one caller, the button in MeetingControls. What
 * had happened is that Machine A's panel was rebuilt twice (two `GET /` in the
 * server's own log, with the user confirming they did not close it).
 *
 * `isPresenting` is not our state at all: it is derived from Live Share
 * (`followModeState?.type === 'activePresenter'`). A rebuild re-runs
 * `bridge.initialize()` and the presenter role goes with it. Nothing persisted
 * or restored it, so the presenter carried on with no sign anything had
 * changed while every follower was stranded on its last page -- which looks
 * exactly like a sync fault from the follower's side.
 *
 * The offer is deliberately NOT an automatic re-assertion. Someone may have
 * legitimately taken over presenting while this panel was down, and taking the
 * meeting back from them without being asked is the same hostility that
 * "never hard-block mid-meeting" rules out elsewhere. One tap, or dismiss it.
 *
 * ## Why sessionStorage, and why keyed by meeting AND surface
 *
 * sessionStorage, not localStorage: it dies with the WebView, and a WebView
 * teardown is a new session where an offer to resume a previous meeting's
 * presentation would be noise. That is the opposite of the choice made for the
 * export/upload counters, which use localStorage precisely BECAUSE they must
 * survive the Android WebView teardown (see DocumentUpload).
 *
 * Keyed by meeting because Teams can reuse the same browsing context across
 * meetings, and keyed by surface because the side panel and the meeting stage
 * are two same-origin iframes in one tab -- they SHARE sessionStorage. A
 * presenter who shares to the stage holds two Live Share connections, only the
 * panel's of which ever presents (see the 2026-09-01 flicker). Without the
 * surface in the key, a rebuild of the presenting panel would also make the
 * stage offer to resume something it never did.
 */

/** The surfaces that can present. Matches the `surface` row of the readout. */
export type PresentingSurface = 'side-panel' | 'stage';

/** The slice of the Storage API this needs. Injected so it can be tested. */
export interface MemoryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function keyFor(surface: PresentingSurface): string {
  return `annoteer.wasPresenting.${surface}`;
}

/**
 * sessionStorage, or null where it is unavailable -- a private window, blocked
 * site data, or a context that throws on access rather than on use. Every
 * caller below tolerates null, so the feature simply does not offer rather
 * than breaking the panel.
 */
export function sessionMemory(): MemoryStorage | null {
  try {
    const storage = window.sessionStorage;
    // Touch it: some contexts throw on the first real access, not on the
    // property read.
    storage.getItem(keyFor('side-panel'));
    return storage;
  } catch {
    return null;
  }
}

/**
 * The document this surface was presenting, so a rebuilt panel can tell that it
 * LOST one from never having had one.
 *
 * Reproduced on two real devices 2026-09-19: Machine A presenting a PDF, phone
 * following, Machine A's panel closed and reopened — and the phone jumped to
 * the whiteboard. Live Share hands the presenter role straight back, the
 * rebuilt panel has no `documentInfo` (React state died with the page, and the
 * panel does not re-fetch), so `SidePanel`'s broadcast effect fires with no
 * document and sends `documentId: 'whiteboard'` — which is the 2026-09-14
 * "the presenter closed the document" signal, and every follower obeys it.
 *
 * ⚠️ **"This panel never held a document" is NOT a sufficient test**, which is
 * the trap in the obvious fix. It is equally true of a rebuilt panel and of a
 * fresh one whose user presses Present with nothing open — and that second case
 * must still broadcast the whiteboard, because that is the 2026-09-12
 * whiteboard feature working as intended. Only a record that OUTLIVES the page
 * can separate them.
 *
 * sessionStorage, like the presenting flag and for the same reason: it must
 * survive a rebuild but not a new meeting.
 */
function documentKeyFor(surface: PresentingSurface): string {
  return `annoteer.presentedDoc.${surface}`;
}

/** Record the document this surface is presenting. */
export function recordPresentedDocument(
  storage: MemoryStorage | null,
  surface: PresentingSurface,
  meetingId: string | null | undefined,
  documentId: string | null | undefined,
): void {
  if (!storage || !meetingId || !documentId) return;
  try {
    // documentId first and split on the FIRST separator, so a meeting id may
    // contain anything; a document id is a uuid or 'whiteboard' and cannot.
    storage.setItem(documentKeyFor(surface), `${documentId}|${meetingId}`);
  } catch {
    // Storage blocked — the guard below simply will not engage.
  }
}

/**
 * Forget it. Called when the surface is presenting with no document, which is a
 * real close: the page is alive and watched it happen.
 */
export function clearPresentedDocument(
  storage: MemoryStorage | null,
  surface: PresentingSurface,
): void {
  if (!storage) return;
  try {
    storage.removeItem(documentKeyFor(surface));
  } catch {
    // As above.
  }
}

/**
 * The document this surface was presenting before the page died, or null.
 * Non-null on a fresh mount means this panel LOST a document to a rebuild and
 * must not tell followers the presenter closed it.
 */
export function presentedDocumentBefore(
  storage: MemoryStorage | null,
  surface: PresentingSurface,
  meetingId: string | null | undefined,
): string | null {
  if (!storage || !meetingId) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(documentKeyFor(surface));
  } catch {
    return null;
  }
  if (!raw) return null;
  const sep = raw.indexOf('|');
  if (sep <= 0) return null;
  // A different meeting's record must not suppress anything here.
  if (raw.slice(sep + 1) !== meetingId) return null;
  return raw.slice(0, sep);
}

/**
 * Whether the Resume offer was ever armed, and whether it ever reached the
 * screen. Added 2026-09-19 because a real meeting could not distinguish the
 * two outcomes that matter: on a rebuild `isPresenting` is false only until
 * the Live Share bridge finishes initializing, so when Live Share hands the
 * presenter role back the strip is replaced by "Presenting" faster than anyone
 * can look. "No strip" therefore means either "correctly silent because the
 * lapse healed itself" or "never rendered at all", and nothing on screen tells
 * them apart.
 *
 * localStorage on purpose, unlike the flag above: this has to survive the very
 * rebuild it is describing, so it can be read afterwards. Same choice, and the
 * same reason, as the export/upload counters.
 *
 * ⚠️ It is CUMULATIVE and never reset, like `mounts` — a timestamp here is the
 * last time it happened on this device, not necessarily this session. Compare
 * it against the clock before reading anything into it.
 */
const ARMED_KEY = 'annoteer.resumeArmed';
const SHOWN_KEY = 'annoteer.resumeShown';

function stamp(now: Date): string {
  return now.toTimeString().slice(0, 8);
}

/** The offer is available: storage says this surface was presenting. */
export function recordResumeArmed(now: Date = new Date()): void {
  try {
    window.localStorage.setItem(ARMED_KEY, stamp(now));
  } catch {
    // Storage blocked. The offer still works; only this diagnostic is lost.
  }
}

/** The strip is actually on screen. */
export function recordResumeShown(now: Date = new Date()): void {
  try {
    window.localStorage.setItem(SHOWN_KEY, stamp(now));
  } catch {
    // As above.
  }
}

/**
 * One readout row. "armed X, shown Y" means it reached the screen. "armed X,
 * NOT shown" must be read beside the `presenting` row: with presenting TRUE it
 * is the healthy case — Live Share handed the role back before the bridge was
 * ready, so there was nothing to resume (seen 2026-09-21). Only with presenting
 * FALSE is it the bug, and then it points at MeetingControls, not storage.
 */
export function resumeOfferTrace(): string {
  let armed: string | null = null;
  let shown: string | null = null;
  try {
    armed = window.localStorage.getItem(ARMED_KEY);
    shown = window.localStorage.getItem(SHOWN_KEY);
  } catch {
    return 'unavailable';
  }
  if (!armed) return 'none';
  return shown ? `armed ${armed}, shown ${shown}` : `armed ${armed}, NOT shown`;
}

/** Record that this surface is presenting in this meeting. */
export function rememberPresenting(
  storage: MemoryStorage | null,
  surface: PresentingSurface,
  meetingId: string | null | undefined,
): void {
  if (!storage || !meetingId) return;
  try {
    storage.setItem(keyFor(surface), meetingId);
  } catch {
    // Storage full or blocked. Nothing to recover: the offer just will not
    // appear, which is the behaviour this feature replaced.
  }
}

/**
 * Forget it. Called on a live `presenting -> not presenting` transition, which
 * is the discriminator this whole feature turns on: a rebuild destroys the
 * page without React ever observing that transition, so seeing it means the
 * user pressed Stop (or someone else took over). Either way it was not a
 * lapse, and there is nothing to offer.
 */
export function forgetPresenting(storage: MemoryStorage | null, surface: PresentingSurface): void {
  if (!storage) return;
  try {
    storage.removeItem(keyFor(surface));
  } catch {
    // As above.
  }
}

/**
 * Whether to offer to resume: this surface was presenting, in THIS meeting,
 * and nothing has since recorded a deliberate stop.
 *
 * There is no expiry. Within one WebView session, in one meeting, "you were
 * presenting and now you are not" is exactly the condition worth surfacing
 * however long the gap; and a timestamp written once at the start of a
 * ninety-minute presentation would be old precisely when the offer matters
 * most.
 */
export function shouldOfferResume(
  storage: MemoryStorage | null,
  surface: PresentingSurface,
  meetingId: string | null | undefined,
): boolean {
  if (!storage || !meetingId) return false;
  try {
    return storage.getItem(keyFor(surface)) === meetingId;
  } catch {
    return false;
  }
}
